import React from 'react';
import { Pressable, StyleSheet, Text, View, type ViewStyle } from 'react-native';

import { navigateToLegal } from '../../core/navigation/navigationRef';
import { colors, spacing, typography } from '../../core/theme/tokens';
import { PRO_SUBSCRIPTION_META } from '../../services/billingService';

type LegalPage = 'Privacy' | 'Terms';

type Props = {
  /** Dismiss an overlay before opening Privacy / Terms (quota modal). */
  onBeforeOpenLegal?: () => void;
};

/**
 * Guideline 3.1.2(c) — functional Privacy Policy and Terms of Use on every
 * paywall, plus subscription name, length, and price.
 */
export function SubscriptionLegalLinks({ onBeforeOpenLegal }: Props) {
  const openLegal = (page: LegalPage) => {
    onBeforeOpenLegal?.();
    navigateToLegal(page);
  };

  return (
    <View style={styles.root}>
      <Text style={styles.meta}>{PRO_SUBSCRIPTION_META}</Text>
      <View style={styles.links}>
        <LegalLink label="Privacy Policy" onPress={() => openLegal('Privacy')} />
        <Text style={styles.dot} accessibilityElementsHidden>
          ·
        </Text>
        <LegalLink label="Terms of Use" onPress={() => openLegal('Terms')} />
      </View>
    </View>
  );
}

function LegalLink({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Pressable
      accessibilityRole="link"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [styles.link, pressed && styles.pressed]}
    >
      <Text style={styles.linkText}>{label}</Text>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  root: {
    marginTop: spacing.sm,
    gap: spacing.xs,
    alignItems: 'center',
  },
  meta: {
    ...typography.caption,
    color: colors.textMuted,
    textAlign: 'center',
  },
  links: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  link: {
    minHeight: 44,
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
    cursor: 'pointer',
  } as ViewStyle,
  linkText: {
    ...typography.caption,
    fontWeight: '600',
    color: colors.textMuted,
    textDecorationLine: 'underline',
  },
  dot: {
    ...typography.caption,
    color: colors.textMuted,
  },
  pressed: {
    opacity: 0.75,
  },
});
